import React from "react";
import { Line } from "react-chartjs-2";
import { AnalyticsFilters } from "../types/analytics.types";
import { useOverviewAnalytics } from "../hooks/useAnalytics";
import { StatCard, ChartCard, LoadingState, ErrorState, EmptyState } from "../design/ui";
import {
  lineOptions,
  lineDataset,
  formatCompact,
  formatPercent,
  formatMinutes,
} from "../design/chartTheme";

interface Props {
  filters: AnalyticsFilters;
}

export const OverviewTab: React.FC<Props> = ({ filters }) => {
  const { data, loading, error, refetch } = useOverviewAnalytics(filters);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!data) return <EmptyState />;

  const { kpis, comparison, trends, top_performers } = data;

  // Stickiness (DAU/MAU) is the single best health signal for a learning app.
  const stickiness =
    kpis.user_metrics.total_monthly_active_users > 0
      ? (kpis.user_metrics.avg_daily_active_users / kpis.user_metrics.total_monthly_active_users) *
        100
      : 0;

  const series = trends?.daily_time_series ?? [];
  const labels = series.map((d) => d.date.slice(5));

  const activityData = {
    labels,
    datasets: [
      lineDataset(
        "Active users",
        series.map((d) => d.active_users),
        0,
        true
      ),
    ],
  };

  const successData = {
    labels,
    datasets: [
      lineDataset(
        "Success rate %",
        series.map((d) => d.success_rate),
        2
      ),
    ],
  };

  return (
    <div>
      {/* North-star metrics */}
      <div className="an-stat-grid">
        <StatCard
          label="Total Users"
          value={kpis.user_metrics.total_unique_users.toLocaleString()}
          sub="Active in range"
          delta={comparison?.user_metrics.total_unique_users_change}
        />
        <StatCard
          label="Avg Daily Active"
          value={Math.round(kpis.user_metrics.avg_daily_active_users).toLocaleString()}
          sub={`${kpis.user_metrics.total_monthly_active_users.toLocaleString()} MAU`}
          delta={comparison?.user_metrics.avg_dau_change}
        />
        <StatCard label="Stickiness" value={formatPercent(stickiness)} sub="DAU / MAU" />
        <StatCard
          label="Success Rate"
          value={formatPercent(kpis.learning_metrics.overall_success_rate)}
          sub="Correct answers"
          delta={comparison?.learning_metrics.success_rate_change}
        />
        <StatCard
          label="Completion Rate"
          value={formatPercent(kpis.progress_metrics.completion_rate)}
          sub={`${kpis.progress_metrics.sections_completed.toLocaleString()} sections done`}
          delta={comparison?.progress_metrics.completion_rate_change}
        />
        <StatCard
          label="Avg Session"
          value={formatMinutes(kpis.session_metrics.avg_session_duration_minutes)}
          sub={`${kpis.session_metrics.avg_sessions_per_user.toFixed(1)} sessions/user`}
        />
        <StatCard
          label="Learning Actions"
          value={formatCompact(kpis.learning_metrics.total_learning_actions)}
          sub={`${Math.round(kpis.learning_metrics.avg_actions_per_user)} per user`}
          delta={comparison?.learning_metrics.total_actions_change}
        />
        <StatCard
          label="Total Sessions"
          value={kpis.session_metrics.total_sessions.toLocaleString()}
          sub="In selected range"
        />
      </div>

      {/* Trends */}
      <div className="an-chart-grid">
        <ChartCard title="Daily Active Users" height={280}>
          {series.length ? (
            <Line data={activityData} options={lineOptions()} />
          ) : (
            <EmptyState title="No activity data" message="" />
          )}
        </ChartCard>
        <ChartCard title="Daily Success Rate" height={280}>
          {series.length ? (
            <Line
              data={successData}
              options={lineOptions({
                scales: {
                  x: { grid: { display: false } },
                  y: { beginAtZero: true, max: 100, grid: { color: "#eef1f6" } },
                },
              })}
            />
          ) : (
            <EmptyState title="No success data" message="" />
          )}
        </ChartCard>
      </div>

      {/* Top performers */}
      <div className="an-cols-2">
        <ChartCard title="Top Sections" subtitle="By learning actions">
          <div className="an-ranklist">
            {top_performers.top_sections.slice(0, 6).map((s, i) => (
              <div key={s.section} className="an-rankrow">
                <span className="an-rank-num">{i + 1}</span>
                <span className="an-rank-name">{s.section}</span>
                <span className="an-rank-value">{s.action_count.toLocaleString()} actions</span>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Language Performance" subtitle="Completion by target language">
          <div className="an-ranklist">
            {top_performers.language_performance.map((l, i) => (
              <div key={l.language} className="an-rankrow">
                <span className="an-rank-num">{i + 1}</span>
                <span className="an-rank-name">{l.language}</span>
                <span className="an-rank-value">
                  {formatPercent(l.completion_rate)} ({l.completed}/{l.total_enrollments})
                </span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default OverviewTab;
